/**
 * CustomerIdleScreen — Premium dark restaurant idle display
 * Layered gradient orbs, bokeh, fine-grid texture, cinematic feel
 * Supports logo upload, idle background image, rotating messages, promo card
 */
import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_MESSAGES = [
  'Welcome',
  'Thank you for dining with us',
  "We're glad you're here",
  'Checkout will appear here when ready',
];

export default function CustomerIdleScreen({
  restaurantName = 'Chain29',
  logoUrl = '',
  idleImageUrl = '',
  tagline = '',
  subtext = 'Please review and pay here when checkout begins',
  accentColor = '#d4a053',
  welcomeMessages,
  promoTitle = '',
  promoText = '',
}) {
  const messages = (welcomeMessages && welcomeMessages.length > 0) ? welcomeMessages : DEFAULT_MESSAGES;
  const [msgIdx, setMsgIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setMsgIdx(i => (i + 1) % messages.length);
        setFade(true);
      }, 700);
    }, 7000);
    return () => clearInterval(interval);
  }, [messages.length]);

  const logoSrc = logoUrl
    ? (logoUrl.startsWith('/api/') ? `${API}${logoUrl}` : logoUrl)
    : null;
  const bgSrc = idleImageUrl
    ? (idleImageUrl.startsWith('/api/') ? `${API}${idleImageUrl}` : idleImageUrl)
    : null;

  return (
    <div className="cfd-idle" data-testid="idle-screen" style={{ '--cfd-accent': accentColor }}>
      {/* === Background Layers === */}

      {/* Idle background image (if uploaded) */}
      {bgSrc && (
        <div className="cfd-idle-bg-image" style={{ backgroundImage: `url(${bgSrc})` }} />
      )}

      {/* Gradient orbs */}
      <div className="cfd-orb cfd-orb-1" />
      <div className="cfd-orb cfd-orb-2" />
      <div className="cfd-orb cfd-orb-3" />

      {/* Vignette */}
      <div className="cfd-vignette" />

      {/* Fine grid texture */}
      <div className="cfd-grid-texture" />

      {/* Floating bokeh particles */}
      <div className="cfd-particles">
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="cfd-bokeh"
            style={{
              left: `${8 + Math.random() * 84}%`,
              top: `${10 + Math.random() * 80}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${8 + Math.random() * 8}s`,
              width: `${3 + Math.random() * 8}px`,
              height: `${3 + Math.random() * 8}px`,
            }}
          />
        ))}
      </div>

      {/* === Content === */}
      <div className="cfd-idle-content">
        {/* Logo */}
        <div className="cfd-logo-plate">
          {logoSrc ? (
            <img src={logoSrc} alt={restaurantName} className="cfd-logo-img" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="cfd-logo-fallback">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
            </svg>
          )}
        </div>

        {/* Restaurant name */}
        <h1 className="cfd-idle-name">{restaurantName}</h1>
        {tagline && <p className="cfd-idle-tagline">{tagline}</p>}

        {/* Divider accent */}
        <div className="cfd-idle-divider">
          <span /><span /><span />
        </div>

        {/* Rotating message */}
        <p className={`cfd-idle-message ${fade ? 'cfd-fade-in' : 'cfd-fade-out'}`}>
          {messages[msgIdx]}
        </p>

        {/* Subtext */}
        {subtext && <p className="cfd-idle-subtext">{subtext}</p>}

        {/* Promo card */}
        {promoTitle && (
          <div className="cfd-promo-card">
            <p className="cfd-promo-title">{promoTitle}</p>
            {promoText && <p className="cfd-promo-text">{promoText}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
